<script>
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

export default {
  components: {
    LoadingState,
  },
  props: {
    config: {
      type: Array,
      default: () => [],
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      hasOpenedAtleastOnce: false,
      iframeLoading: true,
    };
  },
  computed: {
    dashboardAppContext() {
      return {
        conversation: this.currentChat,
        contact: this.$store.getters['contacts/getContact'](this.contactId),
        currentAgent: this.currentAgent,
      };
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    currentAgent() {
      const { id, name, email } = this.$store.getters.getCurrentUser;
      return { id, name, email };
    },
    panelToken() {
      return this.$store.getters['panelToken/getPanelToken'];
    },
    panelOrigin() {
      // Only the Eltafouk comment-panel iframe is trusted with the secret
      // token — never forward it to other configured dashboard-app frames.
      return 'https://api.eltafouk.com';
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.hasOpenedAtleastOnce = true;
        }
      },
    },
    'currentChat.id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.config.forEach((configItem, index) => {
            const frameElement = document.getElementById(
              this.getFrameId(index)
            );
            const targetOrigin = this.getFrameTargetOrigin(configItem);
            if (frameElement && frameElement.contentWindow && targetOrigin) {
              const eventData = {
                event: 'appContext',
                data: this.dashboardAppContext,
              };
              if (targetOrigin === this.panelOrigin) {
                eventData.panelToken = this.panelToken;
              }
              frameElement.contentWindow.postMessage(
                JSON.stringify(eventData),
                targetOrigin
              );
            }
          });
        }
      },
    },
  },
  mounted() {
    window.addEventListener('message', this.triggerEvent);
  },
  unmounted() {
    window.removeEventListener('message', this.triggerEvent);
  },
  methods: {
    getFrameSrc(configItem) {
      return configItem.url || '';
    },
    getFrameTargetOrigin(configItem) {
      try {
        // Resolve relative srcs against the current origin; never fall back to
        // '*' — the postMessage payload carries the panel token.
        return new URL(this.getFrameSrc(configItem), window.location.origin)
          .origin;
      } catch (error) {
        return null;
      }
    },
    triggerEvent(event) {
      if (!this.isVisible) return;
      if (event.data === 'chatwoot-dashboard-app:fetch-info') {
        this.onIframeLoad(0);
      }
    },
    getFrameId(index) {
      return `dashboard-app--frame-${this.position}-${index}`;
    },
    onIframeLoad(index) {
      // A possible alternative is to use ref instead of document.getElementById
      // However, when ref is used together with v-for, the ref you get will be
      // an array containing the child components mirroring the data source.
      const frameElement = document.getElementById(this.getFrameId(index));
      const targetOrigin = this.getFrameTargetOrigin(this.config[index]);
      if (frameElement && frameElement.contentWindow && targetOrigin) {
        const eventData = {
          event: 'appContext',
          data: this.dashboardAppContext,
        };
        if (targetOrigin === this.panelOrigin) {
          eventData.panelToken = this.panelToken;
        }
        frameElement.contentWindow.postMessage(
          JSON.stringify(eventData),
          targetOrigin
        );
      }
      this.iframeLoading = false;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="hasOpenedAtleastOnce" class="dashboard-app--container">
    <div
      v-for="(configItem, index) in config"
      :key="index"
      class="dashboard-app--list"
    >
      <LoadingState
        v-if="iframeLoading"
        :message="$t('DASHBOARD_APPS.LOADING_MESSAGE')"
        class="dashboard-app_loading-container"
      />
      <iframe
        v-if="configItem.type === 'frame' && configItem.url"
        :id="getFrameId(index)"
        :src="getFrameSrc(configItem)"
        @load="() => onIframeLoad(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app--list iframe {
  border: 0;
}
.dashboard-app_loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
</style>
